import { generateUrl } from "../MathFactsGenerator/UrlProcessor/GenerateUrl.tsx";
import {
    CalendarMonthOptions,
    isElapsedTimeQuestion,
    isFractionSubskill,
    isRationalQuestion,
    ignoresNumberPropertyMaxValue,
    isScientificQuestion,
    MissingNumberOptions,
    MoneyQuestionOptions,
    Operations,
    PlaceValueOptions,
    ProblemFormatOptions,
    RationalKindOptions,
    RoundingDisplayOptions,
    ShapeQuestionOptions,
    StoryTypeOptions,
    TimeQuestionOptions,
    VolumeQuestionOptions,
} from "../MathFactsGenerator/Constants.ts";
import { ISettingsProps } from "../MathFactsGenerator/Models.ts";
import { labelFor } from "../MathFactsGenerator/Labels.ts";
import { ladderForSettings, levelName, levelOf } from "../MathFactsGenerator/FactLevels.ts";

/**
 * The worksheets this browser has made recently, so a returning visitor can
 * reopen one without rebuilding it from the settings.
 *
 * Deliberately local to the browser: no account, no address, nothing sent
 * anywhere. The whole record is a handful of URLs the site could already have
 * produced, which is why it can be kept without asking for anything.
 */

const STORAGE_KEY = "mathpages.recentWorksheets";

/**
 * Whether the panel is collapsed. Its own key rather than a field on the list,
 * so a visitor who clears their worksheets does not also lose the preference,
 * and so a parse failure in one cannot take out the other.
 */
const COLLAPSED_KEY = "mathpages.recentWorksheetsCollapsed";
export const MAX_RECENT = 8;

export interface RecentWorksheet {
    url: string;
    label: string;
    savedAt: number;
}

/**
 * Every read and write is guarded. localStorage is absent or throws on access
 * in a private window, with site data blocked, and inside some embedded
 * viewers - none of which should cost the visitor the generator itself.
 */
function readRaw(): RecentWorksheet[] {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return [];
        }
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter(
            (entry) =>
                entry &&
                typeof entry.url === "string" &&
                typeof entry.label === "string" &&
                typeof entry.savedAt === "number"
        );
    } catch {
        return [];
    }
}

/**
 * Fired on `window` whenever this tab changes the list, so the panel can show
 * a sheet as soon as it is saved. The browser's own `storage` event does not
 * cover that: it fires in every *other* tab, never in the one that wrote.
 */
export const RECENT_WORKSHEETS_CHANGED = "mathpages:recentWorksheetsChanged";

function announceChange(): void {
    try {
        window.dispatchEvent(new Event(RECENT_WORKSHEETS_CHANGED));
    } catch {
        // No window to tell, or an old engine; the list is still saved.
    }
}

function writeRaw(entries: RecentWorksheet[]): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
        // Full, disabled, or unavailable. Losing the history is not worth an error.
        return;
    }
    announceChange();
}

/** Most recently saved first. */
export function readRecentWorksheets(): RecentWorksheet[] {
    return readRaw()
        .sort((a, b) => b.savedAt - a.savedAt)
        .slice(0, MAX_RECENT);
}

/** The operations a sheet is built from, in a form fit for a label. */
function operationsLabel(settings: ISettingsProps): string {
    const operations = settings.Operators?.map((operator) => operator.value) ?? [];
    if (operations.length === 0) {
        return "Math facts";
    }
    if (operations.length > 2) {
        return "Mixed facts";
    }
    return operations.map((operation) => labelFor(operation)).join(" & ");
}

/**
 * A short human description of a worksheet, for the list.
 *
 * Named after what the sheet is rather than what the settings are, because
 * "Multiplication, 100 problems" is recognisable a week later and
 * "ppp=100&v1d=1" is not.
 */
export function describeWorksheet(settings: ISettingsProps): string {
    const count = settings.NumberOfProblemsPerPage;
    const pagesCount = settings.NumberOfPages > 1 ? ` · ${settings.NumberOfPages} pages` : "";
    // A differentiation set is three tiers of the sheet below; note it and let
    // the rest of the label describe that sheet.
    const withTiers = settings.DifferentiationSet ? `${pagesCount} · 3 levels` : pagesCount;
    // A worked answer key is worth noting - it changes what the download is.
    const worked =
        settings.AnswerPages && settings.WorkedAnswers ? `${withTiers} · worked key` : withTiers;
    const p = settings.Presentation;
    const scaffold = p?.largePrint
        ? `${worked} · large print`
        : p?.carryBoxes
        ? `${worked} · carry boxes`
        : worked;
    const pages = scaffold;

    switch (settings.Format) {
        case ProblemFormatOptions.TellingTime: {
            if (isElapsedTimeQuestion(settings.TimeQuestion)) {
                const what = settings.CrossNoon ? "Elapsed time across noon" : "Elapsed time";
                return `${what} · ${labelFor(settings.TimeInterval).toLowerCase()} · ${count} problems${pages}`;
            }
            if (settings.TimeQuestion === TimeQuestionOptions.AmOrPm) {
                return `A.M. or P.M. · ${labelFor(settings.TimeInterval).toLowerCase()} · ${count} problems${pages}`;
            }
            const drawing = settings.TimeQuestion === TimeQuestionOptions.DrawTheHands;
            const what = drawing ? "Draw the hands" : "Telling time";
            return `${what} to the ${labelFor(settings.TimeInterval).toLowerCase()} · ${count} clocks${pages}`;
        }
        case ProblemFormatOptions.PlaceValue: {
            const question =
                settings.PlaceValueQuestion === PlaceValueOptions.Mixed
                    ? "Place value"
                    : labelFor(settings.PlaceValueQuestion);
            // The scientific-notation types ignore the digits control (ADR 0130), so it is not named for them.
            if (isScientificQuestion(settings.PlaceValueQuestion)) {
                return `${question} · ${count} questions${pages}`;
            }
            return `${question} · ${settings.PlaceValueDigits} digits · ${count} questions${pages}`;
        }
        case ProblemFormatOptions.RoundingComparing: {
            const line =
                settings.RoundingDisplay === RoundingDisplayOptions.NumberLine ? " · number line" : "";
            return `Rounding & Comparing${line} · ${count} questions${pages}`;
        }
        case ProblemFormatOptions.SpiralReview: {
            const sections = (settings.SpiralSections ?? [])
                .map((s) => `${s.count} ${labelFor(s.format).toLowerCase()}`)
                .join(", ");
            return `Spiral review · ${sections}${pagesCount}`;
        }
        case ProblemFormatOptions.Shapes: {
            // A scale drawing caps its sides at 12 whatever the dial says (ADR 0107), so
            // the dial's "to N" would describe a sheet it did not make.
            const reach = settings.ShapeQuestion === ShapeQuestionOptions.ScaleDrawing ? "" : ` · to ${settings.ShapeMaxSide}`;
            return `Perimeter & area · ${labelFor(settings.ShapeQuestion).toLowerCase()}${reach} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.Volume: {
            // Count the cubes has no edge dial, so there is no "to N" to print.
            const reach =
                settings.VolumeQuestion === VolumeQuestionOptions.CountCubes
                    ? ""
                    : ` · to ${settings.VolumeMaxEdge}`;
            return `Volume · ${labelFor(settings.VolumeQuestion).toLowerCase()}${reach} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.Circles: {
            return `Circles · ${labelFor(settings.CircleQuestion).toLowerCase()} · radius to ${settings.CircleMaxRadius} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.Probability: {
            return `Probability · ${labelFor(settings.ProbabilityQuestion).toLowerCase()} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.PythagoreanTheorem: {
            return `Pythagorean theorem · ${labelFor(settings.PythagoreanQuestion).toLowerCase()} · longest side to ${settings.PythagoreanMaxSide} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.Averages: {
            return `Averages · ${labelFor(settings.AverageQuestion).toLowerCase()} · ${settings.AverageListLength} values · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.BarGraphs: {
            return `Bar graphs · ${labelFor(settings.BarGraphQuestion).toLowerCase()} · to ${settings.BarGraphMaxValue} · ${settings.BarGraphQuestionsPerChart} per chart · ${count} questions${pages}`;
        }
        case ProblemFormatOptions.Ratio: {
            return `Ratio · ${labelFor(settings.RatioQuestion).toLowerCase()} · to ${settings.RatioMaxValue} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.Percent: {
            return `Percent · ${labelFor(settings.PercentQuestion).toLowerCase()} · to ${settings.PercentMaxValue} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.NumberLine: {
            return `Number line · ${labelFor(settings.NumberLineQuestion).toLowerCase()} · to ${settings.NumberLineRange} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.NumberPatterns: {
            return `Number patterns · ${labelFor(settings.PatternQuestion).toLowerCase()} · ${settings.PatternLength} terms · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.CoordinatePlane: {
            return `Coordinate plane · ${labelFor(settings.CoordinateQuestion).toLowerCase()} · ${labelFor(settings.CoordinateGrid).toLowerCase()} · ${count} questions${pages}`;
        }
        case ProblemFormatOptions.Calendar: {
            const month = settings.CalendarMonth && settings.CalendarMonth !== CalendarMonthOptions.Any
                ? ` · ${labelFor(settings.CalendarMonth)}`
                : '';
            return `Calendar · ${labelFor(settings.CalendarQuestion).toLowerCase()}${month} · ${count} questions${pages}`;
        }
        case ProblemFormatOptions.LinePlots: {
            return `Line plots · ${labelFor(settings.LinePlotQuestion).toLowerCase()} · ${labelFor(settings.LinePlotScale).toLowerCase()} · ${count} questions${pages}`;
        }
        case ProblemFormatOptions.Histograms: {
            return `Histograms · ${labelFor(settings.HistogramQuestion).toLowerCase()} · ${count} questions${pages}`;
        }
        case ProblemFormatOptions.BoxPlots: {
            return `Box plots · ${labelFor(settings.BoxPlotQuestion).toLowerCase()} · ${count} questions${pages}`;
        }
        case ProblemFormatOptions.RomanNumerals: {
            return `Roman numerals · ${labelFor(settings.RomanQuestion).toLowerCase()} · to ${settings.RomanMax} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.UnitConversion: {
            return `Unit conversion · ${labelFor(settings.ConversionQuestion).toLowerCase()} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.NumberProperties: {
            // The root types and the exponent rules ignore the largest-number dial, so it is not named for them.
            if (ignoresNumberPropertyMaxValue(settings.NumberPropertyQuestion)) {
                return `Number properties · ${labelFor(settings.NumberPropertyQuestion).toLowerCase()} · ${count} problems${pages}`;
            }
            return `Number properties · ${labelFor(settings.NumberPropertyQuestion).toLowerCase()} · to ${settings.NumberPropertyMaxValue} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.Integers: {
            if (isRationalQuestion(settings.IntegerQuestion)) {
                // Fractions ignore the largest-number dial, so it is not named for them.
                const kind =
                    settings.RationalKind === RationalKindOptions.Fractions
                        ? "fractions"
                        : settings.RationalKind === RationalKindOptions.Decimals
                        ? `decimals to ${settings.IntegerMaxValue}`
                        : `decimals to ${settings.IntegerMaxValue} and fractions`;
                return `Integers · ${labelFor(settings.IntegerQuestion).toLowerCase()} · ${kind} · ${count} problems${pages}`;
            }
            return `Integers · ${labelFor(settings.IntegerQuestion).toLowerCase()} · to ${settings.IntegerMaxValue} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.Algebra: {
            return `Algebra · ${labelFor(settings.AlgebraQuestion).toLowerCase()} · to ${settings.AlgebraMaxValue} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.OrderOfOperations: {
            return `Order of operations · ${labelFor(settings.OrderOfOperationsLevel).toLowerCase()} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.FactFamily: {
            return `Fact families · ${labelFor(settings.FactFamilyQuestion).toLowerCase()} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.Money: {
            if (settings.MoneyQuestion === MoneyQuestionOptions.CountingCoins) {
                return `Counting coins · ${count} problems${pages}`;
            }
            if (settings.MoneyQuestion === MoneyQuestionOptions.MakingChange) {
                return `Making change · ${count} problems${pages}`;
            }
            if (settings.MoneyQuestion === MoneyQuestionOptions.WordProblems) {
                return `Money word problems · ${operationsLabel(settings)} · ${count} problems${pages}`;
            }
            if (settings.MoneyQuestion === MoneyQuestionOptions.Mixed) {
                return `Money (coins & change) · ${count} problems${pages}`;
            }
            return `Money · ${operationsLabel(settings)} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.RiddleDecoder: {
            // One riddle per page; the count is the riddle's own (ADR 0088 §5).
            return `Riddle decoder · ${operationsLabel(settings)}${pages}`;
        }
        case ProblemFormatOptions.MagicSquares: {
            return `Magic squares · to ${settings.MagicSquareMax} · ${count} squares${pages}`;
        }
        case ProblemFormatOptions.ColorByNumber: {
            // One picture per page; the per-page count is the picture's, not a setting.
            return `Color by number · ${operationsLabel(settings)} · border collie${pages}`;
        }
        case ProblemFormatOptions.Decimals: {
            return `Decimals · ${operationsLabel(settings)} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.Fractions: {
            if (isFractionSubskill(settings.FractionQuestion)) {
                return `Fractions · ${labelFor(settings.FractionQuestion).toLowerCase()} · ${count} problems${pages}`;
            }
            return `Fractions · ${operationsLabel(settings)} · ${count} problems${pages}`;
        }
        case ProblemFormatOptions.WordProblem: {
            const spanish = settings.Language === 'es' ? ' (Spanish)' : '';
            const varied = settings.StoryTypes === StoryTypeOptions.Varied ? " · varied stories" : "";
            return `${operationsLabel(settings)} word problems${spanish}${varied} · ${count} per page${pages}`;
        }
        default: {
            const timed = settings.Timed ? " · timed" : "";
            const longDivision = settings.Operators?.some(
                (operator) => operator.value === Operations.LongDivision
            );
            const missing =
                settings.MissingNumber && settings.MissingNumber !== MissingNumberOptions.Off
                    ? " (missing number)"
                    : "";
            const what = longDivision ? "Long division" : operationsLabel(settings);
            // A level test says which level: "Multiplication · level 9 (×7)", and a home
            // practice sheet (ADR 0071 §8) says so after it.
            const level = levelOf(settings);
            const levelText = level
                ? ` · level ${level.id} (${levelName(level, ladderForSettings(settings)!.id)})` +
                  (settings.FactLevelSheet === "practice" ? " · home practice" : "")
                : "";
            return `${what}${levelText}${missing} · ${count} problems${timed}${pages}`;
        }
    }
}

/**
 * What makes two entries the same worksheet: the link's query string - every
 * setting and the seed - and not the page it opens on.
 *
 * An entry reopens on the page it was made on, so its URL carries that page's
 * path. The path is where the sheet is shown, not what the sheet is: the same
 * settings and seed print the same PDF on any page. Comparing whole URLs would
 * list one sheet once per page it had been made on. It also means an entry
 * stored before paths were kept - a home-page URL - is still recognised as
 * the sheet it is.
 */
function sheetKey(url: string): string {
    const query = url.indexOf("?");
    return query === -1 ? url : url.slice(query);
}

/**
 * Records a worksheet, newest first, keeping the list short and free of
 * duplicates. The link's query is the identity (`sheetKey`): two sheets that
 * open the same way are the same sheet, however far apart they were made.
 *
 * `path` is the page the sheet was made on, so the entry reopens there rather
 * than on the home page - the landing page reads the link back the same way it
 * reads a copied one (`settingsForSharedLink`). A sheet made again on another
 * page stays one entry, and reopens on the page it was made on last.
 */
export function rememberWorksheet(settings: ISettingsProps, seed?: string, path?: string): RecentWorksheet[] {
    let url: string;
    try {
        url = generateUrl(settings, seed, path);
    } catch {
        return readRecentWorksheets();
    }

    const entry: RecentWorksheet = {
        url,
        label: describeWorksheet(settings),
        savedAt: Date.now(),
    };

    const key = sheetKey(url);
    const kept = readRaw().filter((existing) => sheetKey(existing.url) !== key);
    const updated = [entry, ...kept]
        .sort((a, b) => b.savedAt - a.savedAt)
        .slice(0, MAX_RECENT);

    writeRaw(updated);
    return updated;
}

/** Removes one worksheet from the list. */
export function forgetWorksheet(url: string): RecentWorksheet[] {
    const remaining = readRaw().filter((entry) => entry.url !== url);
    writeRaw(remaining);
    return remaining.sort((a, b) => b.savedAt - a.savedAt).slice(0, MAX_RECENT);
}

/** Empties the list. */
export function clearRecentWorksheets(): RecentWorksheet[] {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing to do; the list is best effort.
        return [];
    }
    announceChange();
    return [];
}


/**
 * Whether the visitor has collapsed the recent-worksheets panel.
 *
 * **Defaults to collapsed**, so the stored value answers "has this visitor
 * opened it" rather than "has this visitor closed it". The panel is a
 * returning visitor's history, not part of making a worksheet, and at
 * `MAX_RECENT` of 8 it is a few hundred pixels of a phone screen between the
 * settings and everything after them; the heading still says what is in there
 * and carries the count.
 *
 * The test is `!== "0"` rather than `=== "1"` so that an absent key and a
 * corrupted one both collapse. Guarded like every other read here -
 * localStorage throws in a private window and with site data blocked, and a
 * preference is not worth an error (see `readRaw`).
 */
export function readRecentCollapsed(): boolean {
    try {
        return window.localStorage.getItem(COLLAPSED_KEY) !== "0";
    } catch {
        return true;
    }
}

/** Remembers the choice, so collapsing it once is not undone by a reload. */
export function writeRecentCollapsed(collapsed: boolean): void {
    try {
        window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
        // Same reasoning as writeRaw: losing the preference is not worth an error.
    }
}
